/**
 * Onboarding client — mirrors `apps/api/src/domains/onboarding/routes.ts`
 * verbatim (Task 23; CWOS §23).
 *
 * Reads need `onboarding:read`; completing a step needs `onboarding:write`.
 * Both are org_admin permissions in the default seed. `orgId` is never a
 * parameter here: the API takes it from the session, so this client has no way
 * to ask about somebody else's organization.
 *
 * ONBOARDING IS A SEQUENCE, NOT A SET OF TOGGLES. The fourteen steps of
 * {@link ONBOARDING_STEPS} are ordered, and {@link completeStep} refuses a step
 * whose predecessors are unfinished (422 `PREREQUISITE_NOT_MET`). Most steps
 * are also CHECKED against the database — "programs configured" means programs
 * exist — so a 422 `VALIDATION_FAILED` is the server saying the underlying work
 * has not actually been done, not that the request was malformed. Show its
 * message: it names the condition. The one exception is idempotency, which is
 * not an error at all: completing an already-complete step answers 200 with the
 * existing record.
 *
 * {@link signup} IS THE ODD ONE OUT and most deployments will never call it. It
 * is unauthenticated, it creates a whole organization, and it is DISABLED
 * unless the deployment sets `PUBLIC_SIGNUP_ENABLED` — in which state it
 * answers 404, deliberately, so that an anonymous caller cannot even learn the
 * endpoint exists (a 403 would confirm it). Treat a 404 from it as "self-service
 * signup is not offered here", not as a bug.
 */
import { get, post } from './transport.js';
/**
 * The fourteen onboarding steps, in sequence order — the same list, in the same
 * order, as `onboardingStepSchema` on the API. The order is the contract:
 * `completeStep` walks it to decide what counts as a prerequisite.
 */
export const ONBOARDING_STEPS = [
    'ORGANIZATION_CREATED',
    'ADMIN_INVITED',
    'LOCATIONS_CONFIGURED',
    'PROGRAMS_CONFIGURED',
    'EHR_CONNECTED',
    'INITIAL_SYNC_COMPLETE',
    'WORKERS_REVIEWED',
    'CLIENTS_REVIEWED',
    'HOURS_CONFIGURED',
    'RULES_CONFIGURED',
    'POLICIES_CONFIGURED',
    'WORKERS_INVITED',
    'SIMULATION_COMPLETE',
    'LIVE',
];
/**
 * `POST /api/onboarding/signup` — public, unauthenticated; creates an
 * organization and invites its first administrator.
 *
 * 404 when the deployment has not set `PUBLIC_SIGNUP_ENABLED` — see this
 * module's header. NOT IDEMPOTENT: every accepted call creates another
 * organization, so a caller is responsible for preventing a double submit.
 */
export function signup(input, options) {
    return post('/api/onboarding/signup', input, options);
}
/**
 * `GET /api/onboarding/status` — needs `onboarding:read`. The whole picture:
 * where the organization is, what is done, and the full checklist.
 */
export function status(options) {
    return get('/api/onboarding/status', options);
}
/**
 * `GET /api/onboarding/checklist` — needs `onboarding:read`. The same fourteen
 * items {@link status} already carries, without the summary around them. A
 * screen showing both should call {@link status} once rather than both.
 */
export async function checklist(options) {
    const body = await get('/api/onboarding/checklist', options);
    return body.checklist;
}
/**
 * `POST /api/onboarding/complete/:step` — needs `onboarding:write`. Returns the
 * completed item, including who completed it and when.
 *
 * Idempotent (200 with the existing record). Refused with 422 when a
 * prerequisite step is outstanding, when the step's own database condition is
 * not met, or — for `ORGANIZATION_CREATED` — because the server derives it from
 * the organization's own existence and it cannot be advanced by hand.
 */
export function completeStep(step, options) {
    return post(`/api/onboarding/complete/${step}`, {}, options);
}
//# sourceMappingURL=onboarding.js.map